import { useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Plus, Search } from "lucide-react"
import { CHANNEL_LABELS, enabledStages, isOverdue, stageLabel } from "../../shared/domain"
import { Avatar, Empty, ErrorBox, Loading, SeverityBadge, StatusBadge } from "@/components/common"
import { NewComplaintDialog } from "@/components/NewComplaintDialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { NativeSelect } from "@/components/ui/native-select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import type { ComplaintFilters } from "@/lib/api"
import { useApp } from "@/lib/app-context"
import { fmtDate, useAsync } from "@/lib/format"
import { cn } from "@/lib/utils"

const PAGE = 25

const STATUS_CHIPS: Array<{ key: string; label: string }> = [
  { key: "", label: "Todas" },
  { key: "open", label: "Abertas" },
  { key: "overdue", label: "Prazo ultrapassado" },
  { key: "closed", label: "Encerradas" },
]

export function ComplaintsPage() {
  const { api, settings, users, go, userName, severityLabel } = useApp()
  const [params] = useSearchParams()
  // `?entity_id=` vem de Clientes ("Ver reclamações"): todas as do cliente.
  const [filters, setFilters] = useState<ComplaintFilters>(() => {
    const entity = params.get("entity_id")
    return entity ? { entity_id: entity } : { status: "open" }
  })
  const [q, setQ] = useState("")
  const [page, setPage] = useState(0)
  const [creating, setCreating] = useState(false)
  const { data, error, loading, reload } = useAsync(() => api.complaints(filters), [api, JSON.stringify(filters)])

  const set = (patch: ComplaintFilters) => {
    setPage(0)
    setFilters((f) => ({ ...f, ...patch }))
  }

  const items = useMemo(() => {
    const term = q.trim().toLowerCase()
    const all = data?.items ?? []
    if (!term) return all
    return all.filter((c) =>
      [c.ref, c.submitted_name, c.submitted_email, c.description, c.category, c.invoice_ref].some((v) =>
        (v ?? "").toLowerCase().includes(term),
      ),
    )
  }, [data, q])
  const pages = Math.max(1, Math.ceil(items.length / PAGE))
  const shown = items.slice(page * PAGE, page * PAGE + PAGE)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">Reclamações</h2>
          <p className="text-xs text-muted-foreground">Portal, email e registo interno</p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> Registar reclamação
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {STATUS_CHIPS.map((s) => (
          <button
            key={s.key}
            onClick={() => set({ status: s.key || undefined })}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              (filters.status ?? "") === s.key
                ? "border-transparent bg-accent text-accent-foreground"
                : "border-border/60 bg-card text-muted-foreground hover:text-foreground",
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        <div className="relative lg:col-span-2">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Referência, cliente, descrição, fatura…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setPage(0)
            }}
          />
        </div>
        <NativeSelect value={filters.stage ?? ""} onChange={(e) => set({ stage: e.target.value || undefined })}>
          <option value="">Todas as etapas</option>
          {enabledStages(settings.stages).map((s) => (
            <option key={s} value={s}>
              {stageLabel(settings.stages, s)}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect value={filters.category ?? ""} onChange={(e) => set({ category: e.target.value || undefined })}>
          <option value="">Todos os motivos</option>
          {settings.categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </NativeSelect>
        <div className="grid grid-cols-2 gap-2">
          <NativeSelect value={filters.severity ?? ""} onChange={(e) => set({ severity: e.target.value || undefined })}>
            <option value="">Gravidade</option>
            {settings.severities.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect value={filters.owner_user_id ?? ""} onChange={(e) => set({ owner_user_id: e.target.value || undefined })}>
            <option value="">Responsável</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </NativeSelect>
        </div>
      </div>

      {filters.entity_id ? (
        <p className="text-xs text-muted-foreground">
          Só do cliente selecionado ·{" "}
          <button className="underline" onClick={() => set({ entity_id: undefined })}>
            mostrar todos
          </button>
        </p>
      ) : null}

      {error ? <ErrorBox message={error} onRetry={reload} /> : null}
      {loading && !data ? (
        <Loading />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border/60 bg-card shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Referência</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Gravidade</TableHead>
                <TableHead className="hidden lg:table-cell">Canal</TableHead>
                <TableHead>Responsável</TableHead>
                <TableHead>Prazo</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((c) => (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => go(`/reclamacoes/${c.id}`)}>
                  <TableCell className="font-medium">{c.ref}</TableCell>
                  <TableCell className="max-w-[14rem] truncate">{c.submitted_name ?? "—"}</TableCell>
                  <TableCell className="max-w-[12rem] truncate">{c.category ?? "—"}</TableCell>
                  <TableCell>
                    <SeverityBadge severity={c.severity} label={severityLabel(c.severity) ?? undefined} />
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground lg:table-cell">{CHANNEL_LABELS[c.channel]}</TableCell>
                  <TableCell>
                    {c.owner_user_id ? (
                      <span className="flex items-center gap-1.5">
                        <Avatar name={userName(c.owner_user_id)} />
                        <span className="hidden truncate xl:inline">{userName(c.owner_user_id)}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className={cn("tabular-nums", isOverdue(c) && "font-medium text-red-600")}>{fmtDate(c.due_at)}</TableCell>
                  <TableCell>
                    <StatusBadge c={c} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {shown.length === 0 ? <Empty>Nenhuma reclamação com estes filtros.</Empty> : null}
        </div>
      )}

      {items.length > 0 ? (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            A mostrar {shown.length} de {items.length} reclamações{data?.truncated ? " (limite de leitura atingido)" : ""}
          </span>
          {pages > 1 ? (
            <span className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </Button>
              <span className="px-2 py-1.5">
                {page + 1} / {pages}
              </span>
              <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>
                Seguinte
              </Button>
            </span>
          ) : null}
        </div>
      ) : null}

      <NewComplaintDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(c) => {
          setCreating(false)
          go(`/reclamacoes/${c.id}`)
        }}
      />
    </div>
  )
}
